import * as React from 'react';
import { useCallback, useMemo, useState, useContext, useEffect } from 'react';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import * as moment from 'moment-timezone';
import FormattedMomentUtils from '../utils/formatted-moment-utils';
import { getComponentElementKeyValue } from '../utils/key-value-getter';
import { BaseFormStateContext } from '../BaseForm';
import { ComponentInputElement } from '../../types/common/item-definition-types';
import { ItemValueKeySuffix } from '../../enums/common/item-value-key-suffix';
import DateUtils from '../../utils/date-utils';
import { basicSiteVariablesDefinition, ifBaseApiPath } from '../../config/baseConfig';

interface EntryDateElementProps {
  componentElement: ComponentInputElement;
}

const useStyles = makeStyles(
  createStyles({
    calendar: {
      '& .MuiPickersDay-daySelected': {
        color: 'white'
      }
    }
  })
);

const EntryDateElement: React.FC<EntryDateElementProps> = ({ componentElement }) => {
  const styles = useStyles({});
  const { inputValues, updateInputValues, applicationParams } = useContext(BaseFormStateContext);

  const projectId = basicSiteVariablesDefinition.projectId;

  const valueKey = useMemo(() => componentElement.valueKey, []);
  const valueHasErrorKey = useMemo(() => valueKey + ItemValueKeySuffix.HasError, []);

  const today: string = useMemo(() => DateUtils.strToday(), []);

  const [apiNoEntryDates, setApiNoEntryDates] = useState<string[]>([]);

  useEffect(() => {
    // 申込み不可日取得API呼び出し
    if (!projectId || !componentElement.noEntryDateServiceType) {
      setApiNoEntryDates([]);
      return;
    }

    const entryType = componentElement.noEntryDateServiceType;
    const startDate = moment().format('YYYY-MM-DD');
    const endDate = moment().add(6, 'months').format('YYYY-MM-DD');

    const queryParams = new URLSearchParams({
      entryType,
      startDate,
      endDate
    });

    const endpoint = `${ifBaseApiPath}/project/${projectId}/no-entry-dates?${queryParams}`;

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    };
    const method = 'GET';

    fetch(endpoint, {
      method,
      headers,
      mode: 'cors'
    })
      .then(res => {
        if (res.status !== 200) {
          return;
        }
        return res.json();
      })
      .then(data => {
        if (data) {
          setApiNoEntryDates(data.noEntryDates || []);
        } else {
          setApiNoEntryDates([]);
        }
      })
      .catch(error => {
        console.error('No entry dates API error:', error);
        // API失敗時は制限なし（空配列）
        setApiNoEntryDates([]);
      });
  }, [projectId, componentElement.noEntryDateServiceType]);

  const noEntryDatesSet = useMemo(() => new Set(apiNoEntryDates), [apiNoEntryDates]);

  // minDateの計算（申込み不可日を考慮）
  const minDate: string | undefined = useMemo(() => {
    return DateUtils.calcMinDate(
      componentElement.minDateOffset,
      componentElement.useBusinessDayOffset,
      noEntryDatesSet,
      today
    );
  }, [componentElement.minDateOffset, componentElement.useBusinessDayOffset, noEntryDatesSet, today]);

  const maxDate: string | undefined = useMemo(() => {
    if (!componentElement.maxDateOffset) {
      return undefined;
    } else if (componentElement.useBusinessDayOffset) {
      return DateUtils.businessDaysToTargetDate(componentElement.maxDateOffset);
    } else {
      return DateUtils.offsetDaysToTargetDate(componentElement.maxDateOffset);
    }
  }, [componentElement.maxDateOffset, componentElement.useBusinessDayOffset]);

  // 初期値の更新
  useEffect(() => {
    const currentValue = inputValues[valueKey];
    if (currentValue) return;
    if (!currentValue || currentValue !== minDate) {
      updateInputValues(valueKey, minDate || today);
    }
  }, [minDate, valueKey]);

  const inputValue = useMemo(
    () =>
      getComponentElementKeyValue({
        inputValues,
        applicationParams,
        inputValueType: componentElement.valueType,
        valueKey: valueKey,
        initialValue: componentElement.initialValue,
        converter: componentElement.converter
      }),
    [
      inputValues[componentElement.valueKey],
      applicationParams[componentElement.valueKey],
      componentElement.valueType,
      valueKey,
      componentElement.initialValue,
      minDate,
      today
    ]
  );

  const [errorMessage, setErrorMessage] = useState('');

  const handleDateChange = useCallback(
    e => {
      // エラーメッセージをクリア
      updateInputValues(`${valueKey}HasError`, false);
      setErrorMessage("");
      if (!e || !moment(e).isValid()) {
        setErrorMessage('有効な日付を入力してください。');
        updateInputValues(`${valueKey}HasError`, true);
        updateInputValues(valueKey, "Invalid date");
        return;
      }
      updateInputValues(valueKey, e && e.format(basicSiteVariablesDefinition.linkageDateFormat));
    },
    [valueKey, updateInputValues, setErrorMessage]
  );

  const handleOnError = useCallback(() => {
    let error = true;
    if (!inputValue || inputValue === 'Invalid date') {
      setErrorMessage('有効な日付を入力してください。');
      updateInputValues(`${valueKey}HasError`, true);
    } else if (minDate && moment(inputValue).isBefore(minDate)) {
      setErrorMessage(`${minDate}以降の日付を入力してください`);
      updateInputValues(`${valueKey}HasError`, true);
    } else if (maxDate && moment(inputValue).isAfter(maxDate)) {
      setErrorMessage(`${maxDate}以前の日付を入力してください`);
      updateInputValues(`${valueKey}HasError`, true);
    } else if (noEntryDatesSet.has(inputValue)) {
      setErrorMessage('選択できない日付です。');
      updateInputValues(`${valueKey}HasError`, true);
    } else {
      error = false;
      setErrorMessage('');
      updateInputValues(`${valueKey}HasError`, false);
    }
    updateInputValues(valueHasErrorKey, error);
  }, [minDate, maxDate, inputValue, noEntryDatesSet]);

  return (
    <MuiPickersUtilsProvider utils={FormattedMomentUtils}>
      <KeyboardDatePicker
        margin="normal"
        color="primary"
        className={styles.calendar}
        id={`date-picker-dialog-${valueKey}`}
        format="YYYY/MM/DD"
        InputProps={{ readOnly: true }}
        helperText={errorMessage}
        value={inputValue === '' ? null : inputValue}
        onChange={handleDateChange}
        KeyboardButtonProps={{
          'aria-label': 'change date'
        }}
        cancelLabel={<span style={{ color: 'black' }}>キャンセル</span>}
        error={!!errorMessage}
        onError={handleOnError}
        disableToolbar
        style={{
          width: '100%',
          ...componentElement.styleOption
        }}
        inputVariant="outlined"
        minDate={minDate ? moment(minDate).toDate() : undefined}
        maxDate={maxDate ? moment(maxDate).toDate() : undefined}
        shouldDisableDate={React.useCallback((date) => {
          const dateStr = moment(date).format('YYYY-MM-DD');
          return noEntryDatesSet.has(dateStr);
        }, [noEntryDatesSet])}
      />
    </MuiPickersUtilsProvider>
  );
};

export default React.memo(EntryDateElement);
